import React, { useContext, useEffect, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import SanitizeHTML from "sanitize-html";

import {
  Button,
  ButtonIcon,
  Caption,
  Flex,
  Modal,
  RadioGroup,
  Textarea,
} from "@olist/design-system";
import { ChatOutline } from "@olist/design-system-icons";

import { localHtml, sanitizedHtmlTags } from "./constants";
import { Container, EvaluationRow, SButton } from "./styles";
import { buildBreadcrumbItems } from "./utils";
import { Breadcrumb } from "components/breadcrumb";
import { ErrorHandling } from "components/errorHandling";
import { Loading } from "components/loading";
import { MixpanelContext } from "contexts";
import { productToTitle } from "helpers/formatters";
import { InternalServerError } from "icons/error";
import { ThumbsDown, ThumbsUp } from "icons/thumbs";
import { createComment as createCommentGQL } from "services/gql/createComment";
import { getArticleByPath } from "services/gql/getArticleByPath";
import { getTreeByPath } from "services/gql/getTreeByPath";

const isDevelopment =
  (process.env.REACT_APP_NODE_ENV ?? "production") === "development";

export function Article() {
  const [like, setLike] = useState<"positive" | "negative" | undefined>(
    undefined
  );
  const [isOpen, setIsOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [selectedRadio, setSelectedRadio] = useState("");

  const navigate = useNavigate();
  const params = useParams<{
    product: string;
    category: string;
    "*": string;
  }>();
  const { trackAgent } = useContext(MixpanelContext);

  const articlePath = `${params.product}/${params.category}/${params["*"]}`;
  const { data, error, loading } = useQuery(getArticleByPath, {
    variables: {
      path: articlePath,
    },
  });

  const [createComment] = useMutation(createCommentGQL);

  const {
    data: breadcrumbData,
    error: breadcrumbError,
    loading: breadcrumbLoading,
  } = useQuery(getTreeByPath, {
    variables: {
      path: articlePath,
    },
  });

  const breadcrumbItems = buildBreadcrumbItems({
    data: breadcrumbData?.pages?.treeByPath,
    articlePath,
    product: params.product!,
    onClick: (link) => () => navigate(link),
  });

  const articleResponse = data?.pages?.singleByPath;
  const article = isDevelopment ? localHtml : articleResponse?.content;
  let escapedHtml = SanitizeHTML(article ?? "", sanitizedHtmlTags);

  const trackAction = (text: string) => {
    if (!Object.keys(productToTitle).includes(params.product ?? "")) return;
    const validatedProduct = params.product as keyof typeof productToTitle;

    trackAgent.track(`Central de ajuda - ${productToTitle[validatedProduct]}`, {
      asset: "Central de ajuda",
      page: "article",
      component: "article",
      product: productToTitle[validatedProduct],
      clickedOn: `Artigo - ${text}`,
    });
  };

  const handleLikeButtonClick = (value: "positive" | "negative") => {
    if (like === "negative" && value === "negative") {
      return setLike(undefined);
    }
    if (like === "positive" && value === "positive") {
      return setLike(undefined);
    }
    return setLike(value);
  };

  const handleOpen = () => setIsOpen(!isOpen);

  const sendFeedback = () => {
    createComment({
      variables: {
        pageId: articleResponse.id,
        comment: `${like} | ${selectedRadio} | ${comment}`,
      },
      onCompleted: () => {
        const validatedProduct = params.product as keyof typeof productToTitle;
        trackAgent.track(`Central de ajuda - Avaliação de Artigo - Feedback`, {
          asset: "Central de ajuda",
          page: "article",
          component: "article",
          product: productToTitle[validatedProduct],
          article: `Artigo - ${
            breadcrumbItems.at(-1)?.text || articleResponse.id
          }`,
          feedback: `${like} | ${selectedRadio} | ${comment}`,
          evaluation: like,
        });
      },
    });
    handleOpen();
  };

  useEffect(() => {
    if (!loading && !error) {
      const hash = window.location.hash.substring(1);
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    }
  }, [loading, error]);

  useEffect(() => {
    if (articleResponse && !loading) {
      document.title = articleResponse?.title;
      trackAction(articleResponse.title);
    }
  }, [articleResponse, loading]);

  if (!isDevelopment && loading) return <Loading />;
  if (!isDevelopment && error)
    return (
      <ErrorHandling
        image={<InternalServerError />}
        title="Algo inesperado aconteceu"
        subtitle="Ocorreu um erro inesperado ao buscar o artigo, mas fomos notificados!"
      />
    );

  const oembedRegex = /<oembed[^>]*>/g;
  const oembedMatch = escapedHtml.match(oembedRegex);

  if (oembedMatch && oembedMatch.length > 0) {
    const oembedUrl = (oembedMatch as any)[0].match(/url="([^"]*)"/)[1];
    const iframeElement = `<iframe src="${oembedUrl}" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    escapedHtml = escapedHtml.replace(oembedRegex, iframeElement);
  }

  const shouldRenderBreadcrumb =
    !!breadcrumbItems || !!breadcrumbLoading || !!breadcrumbError;

  return (
    <>
      <Breadcrumb
        items={breadcrumbItems}
        shouldRender={shouldRenderBreadcrumb}
      />
      <Flex width="100%" justifyContent="flex-end">
        <Caption>
          Última atualização:{" "}
          {new Date(articleResponse?.updatedAt || "").toLocaleDateString(
            "pt-BR"
          )}
        </Caption>
      </Flex>
      <Container dangerouslySetInnerHTML={{ __html: escapedHtml }} />
      <EvaluationRow>
        <Flex gap="inline.small" alignItems="center">
          <Caption bold>Este artigo foi útil?</Caption>
          <ButtonIcon
            icon={<ThumbsUp isActive={like === "positive"} />}
            onClick={() => {
              handleLikeButtonClick("positive");
              const validatedProduct =
                params.product as keyof typeof productToTitle;
              trackAgent.track(`Central de ajuda - Avaliação de Artigo`, {
                asset: "Central de ajuda",
                page: "article",
                component: "article",
                product: productToTitle[validatedProduct],
                article: `Artigo - ${
                  breadcrumbItems.at(-1)?.text || articleResponse.id
                }`,
                evaluation: "positive",
              });
            }}
          />
          <ButtonIcon
            icon={<ThumbsDown isActive={like === "negative"} />}
            onClick={() => {
              handleLikeButtonClick("negative");
              const validatedProduct =
                params.product as keyof typeof productToTitle;
              trackAgent.track(`Central de ajuda - Avaliação de Artigo`, {
                asset: "Central de ajuda",
                page: "article",
                component: "article",
                product: productToTitle[validatedProduct],
                article: `Artigo - ${
                  breadcrumbItems.at(-1)?.text || articleResponse.id
                }`,
                evaluation: "negative",
              });
            }}
          />
        </Flex>
        <SButton
          variation="secondary"
          iconLeft={ChatOutline}
          onClick={handleOpen}
          disabled={like !== "negative"}
        >
          Dê sua opinião sobre o artigo
        </SButton>
      </EvaluationRow>
      <Modal
        isOpen={isOpen}
        onClose={handleOpen}
        title="Como podemos melhorar este artigo?"
        size="small"
        body={
          <Flex
            gap="stack.medium"
            flexDirection="column"
            paddingLeft="inline.1xsmall"
          >
            <RadioGroup
              name="preview"
              onChange={(event) => setSelectedRadio(event.target.value)}
              selectedRadio={selectedRadio}
            >
              <RadioGroup.Radio
                value="Impreciso"
                label="Impreciso, não corresponde ao cenário atual"
              />
              <RadioGroup.Radio
                value="Dificil_entendimento"
                label="Difícil de entender, pouco claro e esclarecedor"
              />
              <RadioGroup.Radio
                value="Informacoes_ausentes"
                label="Informações ausentes, relevantes ou abrangentes"
              />
              <RadioGroup.Radio
                value="Erros_formatação"
                label="Erros de formatação, digitação e/ou links quebrados"
              />
              <RadioGroup.Radio value="Outros" label="Outros" />
            </RadioGroup>
            <Textarea
              label="Compartilhe informações e sugestões adicionais"
              onChange={({ target }) => setComment(target.value)}
            />
          </Flex>
        }
        footer={
          <Flex justifyContent="space-between" width="100%">
            <Button variation="secondary" onClick={handleOpen}>
              cancelar
            </Button>
            <Button onClick={sendFeedback} disabled={selectedRadio === ""}>
              enviar
            </Button>
          </Flex>
        }
      />
    </>
  );
}
